import React, { createContext, useState } from 'react';
import { type StoreApi, createStore } from 'zustand';

import type { IAudit } from '@ifs-gcp/core';

export interface AuditState {
  audit: IAudit | null;
  lastSavedAuditBtoa: string | null;
  isActionPending: boolean;
  _pendingActionCount: number;
  setAudit: (audit: IAudit) => void;
  setLastSavedAuditBtoa: (lastSavedAuditBtoa: string | null) => void;
  incrementPendingActions: () => void;
  decrementPendingActions: () => void;
  resetAudit: () => void;
}

export type AuditStore = StoreApi<AuditState>;

interface AuditStoreProviderProps {
  children: React.ReactNode;
}

export const AuditStoreContext = createContext<AuditStore | null>(null);

const applyIncrement = (state: AuditState) => ({
  _pendingActionCount: state._pendingActionCount + 1,
  isActionPending: true as const,
});

const applyDecrement = (state: AuditState) => {
  const count = Math.max(0, state._pendingActionCount - 1);

  return {
    _pendingActionCount: count,
    isActionPending: count > 0,
  };
};

export const AuditStoreProvider = ({ children }: AuditStoreProviderProps) => {
  const [store] = useState(() => {
    return createStore<AuditState>((set) => ({
      audit: null,
      lastSavedAuditBtoa: null,
      isActionPending: false,
      _pendingActionCount: 0,
      setLastSavedAuditBtoa: (lastSavedAuditBtoa) => set({ lastSavedAuditBtoa }),
      setAudit: (audit) => set({ audit }),
      incrementPendingActions: () => set(applyIncrement),
      decrementPendingActions: () => set(applyDecrement),
      resetAudit: () =>
        set({
          audit: null,
          lastSavedAuditBtoa: null,
          isActionPending: false,
          _pendingActionCount: 0,
        }),
    }));
  });

  return <AuditStoreContext.Provider value={store}>{children}</AuditStoreContext.Provider>;
};
